// src/components/AppLayout.tsx
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { NavbarTop } from './NavbarTop';
import { NavbarMinimalColored } from './NavbarMinimalColored';
import ChatWidget from './ChatWidget';
import './AppLayout.css';

const MOBILE_BREAKPOINT = '(max-width: 768px)';

function sidebarWidthForLayout(
  isMobile: boolean,
  mobileSidebarOpen: boolean,
  sidebarCollapsed: boolean,
): string {
  if (isMobile) {
    return mobileSidebarOpen ? '220px' : '0px';
  }
  return sidebarCollapsed ? '70px' : '200px';
}

const AppLayout: React.FC = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; link_url?: string } | null;
      if (data?.type === 'notification-navigate' && data.link_url) {
        navigate(data.link_url);
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen((prev) => !prev);
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const sidebarExpanded = isMobile ? mobileSidebarOpen : !sidebarCollapsed;
  const sidebarWidth = sidebarWidthForLayout(isMobile, mobileSidebarOpen, sidebarCollapsed);

  return (
    <div className={`app-layout${isMobile ? ' app-layout-mobile' : ''}`}>
      <header className="header">
          <NavbarTop onToggleSidebar={handleToggleSidebar} />
      </header>
      {isMobile && mobileSidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={closeMobileSidebar}
        />
      )}
      <aside
        className={`sidebar${isMobile && !mobileSidebarOpen ? ' sidebar-hidden' : ''}`}
        style={{ width: sidebarWidth }}
      >
        <NavbarMinimalColored
          collapsed={!sidebarExpanded}
          onNavigate={closeMobileSidebar}
        />
      </aside>
      <main className="content">
        <Outlet />
      </main>
      <ChatWidget />
    </div>
  );
};

export default AppLayout;
